import React from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Check, X, Loader2, GraduationCap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { apiGet, apiPost } from '../utils/api';
import { sanitizeErrorMessage } from '../utils/errorMessages';

// Claims a Zeffert University class seat from an emailed invitation. The invitee
// may not have a JPRO account yet: if signed out we send them to register/login
// and return here to accept (which creates + claims the seat).
export default function AcceptTrainingSeat() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, refreshUser } = useAuth();
  const { theme } = useTheme();
  const d = theme === 'dark';

  const { data: invite, isLoading, error } = useQuery({
    queryKey: ['training-seat-invite', token],
    queryFn: () => apiGet(`/training/seat_invitations/${token}`).then((r) => {
      if (!r.ok) throw new Error('Invitation not found or expired');
      return r.json();
    }),
  });

  const accept = useMutation({
    mutationFn: async () => {
      const res = await apiPost(`/training/seat_invitations/${token}/accept`, {});
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(sanitizeErrorMessage(body.error, 'Could not accept'));
      return body;
    },
    onSuccess: async () => { await refreshUser?.(); navigate('/dashboard/training', { replace: true }); },
  });
  const decline = useMutation({
    mutationFn: async () => {
      const res = await apiPost(`/training/seat_invitations/${token}/decline`, {});
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(sanitizeErrorMessage(body.error, 'Could not decline'));
      return body;
    },
    onSuccess: () => navigate('/', { replace: true }),
  });

  // Send the invitee to login/signup, returning here afterward (the app reads
  // localStorage.jpro_login_redirect after auth).
  const goAuth = (path) => {
    localStorage.setItem('jpro_login_redirect', location.pathname);
    // Stamp the invite timestamp too, or clearStaleInvitationFlags treats the
    // redirect as stale and wipes it during auth teardown (matches sibling invite pages).
    localStorage.setItem('jpro_invite_flag_ts', Date.now().toString());
    navigate(path);
  };

  const wrap = `min-h-screen flex items-center justify-center px-4 ${d ? 'bg-gray-900 text-white' : 'bg-gray-50'}`;
  if (isLoading) return <div className={wrap}><Loader2 className="animate-spin text-blue-500" size={32} /></div>;
  if (error || !invite) {
    return (
      <div className={wrap}>
        <div className="text-center">
          <X size={44} className="mx-auto mb-3 text-red-500" />
          <p className="text-sm">This class invitation is invalid or has expired.</p>
          <Link to="/" className="text-blue-500 text-sm">Go home</Link>
        </div>
      </div>
    );
  }
  const done = invite.status !== 'pending';

  return (
    <div className={wrap}>
      <div className={`max-w-md w-full rounded-2xl border p-6 ${d ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <GraduationCap size={28} className="text-orange-500 mb-3" />
        <h1 className="text-lg font-semibold mb-1">You have a class seat</h1>
        <p className="text-sm text-gray-500 mb-4">
          You have been invited to <strong>{invite.class_title}</strong> for <strong>{invite.email}</strong>.
        </p>
        {done ? (
          <p className="text-sm text-gray-500">This invitation is already {invite.status}.</p>
        ) : !isAuthenticated ? (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">Sign in or create your free JPRO account for {invite.email} to claim your seat.</p>
            <button onClick={() => goAuth('/signup')}
              className="block w-full text-center text-sm font-bold px-4 py-2 rounded-lg bg-orange-500 text-white">Create account</button>
            <button onClick={() => goAuth('/login')}
              className="block w-full text-center text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600">Sign in</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => accept.mutate()} disabled={accept.isPending}
              className="flex-1 inline-flex items-center justify-center gap-1 text-sm font-bold px-4 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40">
              <Check size={16} /> Accept seat
            </button>
            <button onClick={() => decline.mutate()} disabled={decline.isPending}
              className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600">Decline</button>
          </div>
        )}
        {accept.isError && <p className="text-xs text-red-500 mt-3">{accept.error.message}</p>}
      </div>
    </div>
  );
}
